import {ChangeEvent, useEffect, useRef, useState} from 'react';
import {Navigate, useNavigate} from 'react-router-dom';
import {useLatestDefaults, usePostMutations} from '../api/posts';
import Sheet from '../components/feedback/Sheet';
import {SkeletonList} from '../components/feedback/Skeleton';
import Icon from '../components/Icon';
import KindGrid from '../components/tell/KindGrid';
import StepBars from '../components/tell/StepBars';
import TellForm, {TellFormValues} from '../components/tell/TellForm';
import {useAddFollowUp} from '../components/todo/useAddFollowUp';
import {confirm} from '../hooks/useConfirm';
import {toast} from '../hooks/useToast';
import {useTellPrefill} from '../hooks/useTellPrefill';
import {draftHasContent} from '../lib/draftState';
import {draftStore, useDraft} from '../lib/newTellDraft';
import {uploadQueue} from '../lib/uploadQueue';
import {nowLocal, offsetMinutes} from '../lib/when';
import {Kind} from '../types';

// The ten-second tell, three screens sharing one draft (ui/lib/newTellDraft.ts) that survives
// reloads, the camera and the app being closed:
//   1. photo (design/screens/Tell1Photo): the phone's own camera or library, or skip
//      (`?plant=&place=&kind=` from a plant page or the memories prompt fill the draft first)
//   2. kind  (Tell2Kind): one tap
//   3. words (Tell3Words): the text, plant, where, when; Save
// Saving posts the words first; the photos follow through the upload queue.

const MAX_PHOTOS = 10;

// ------------------------------------------------------------------ step 1

export function NewTellPhotoStep() {
    const navigate = useNavigate();
    const {draft, ready} = useDraft();
    useTellPrefill();
    const cameraInput = useRef<HTMLInputElement>(null);
    const libraryInput = useRef<HTMLInputElement>(null);
    const [closing, setClosing] = useState(false);

    const onFiles = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const added = draftStore.addFiles(files, MAX_PHOTOS);
            if (added < files.length) toast.info(`Ten photos is the limit for one tell; kept the first ${MAX_PHOTOS}.`);
            navigate('/tell/new/kind');
        }
        event.target.value = '';
    };

    const close = () => {
        if (ready && draftHasContent(draft)) setClosing(true);
        else navigate('/journal');
    };

    return (
        <div className="fixed inset-0 z-10 flex flex-col bg-[#0E100C] text-[#F3F1EA]">
            <header className="flex items-center justify-between px-3 pb-3 pt-[max(16px,env(safe-area-inset-top))]">
                <button type="button" aria-label="Close" className="flex h-11 w-11 items-center justify-center rounded-pill bg-white/10" onClick={close}>
                    <Icon name="close" size={22} />
                </button>
                <div className="flex flex-col items-center gap-[6px]">
                    <span className="font-display text-[17px] font-bold">New tell</span>
                    <StepBars step={1} dark />
                </div>
                <span className="w-11" />
            </header>

            <div className="relative mx-3 flex flex-1 items-center justify-center overflow-hidden rounded-[28px] bg-[#3A4632]">
                {draft.photos.length > 0 ? (
                    <img src={draft.photos[draft.photos.length - 1].previewUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                    <span className="px-8 text-center text-sm text-white/70">The phone&rsquo;s own camera opens when you tap the shutter.</span>
                )}
                {['top-6 left-6 border-l-2 border-t-2 rounded-tl-[10px]', 'top-6 right-6 border-r-2 border-t-2 rounded-tr-[10px]', 'bottom-6 left-6 border-b-2 border-l-2 rounded-bl-[10px]', 'bottom-6 right-6 border-b-2 border-r-2 rounded-br-[10px]'].map(cls => (
                    <span key={cls} aria-hidden className={`absolute h-7 w-7 border-white/80 ${cls}`} />
                ))}
                <span className="absolute top-5 rounded-pill bg-scrim/45 px-3 py-1 text-xs font-semibold">
                    {draft.photos.length > 0 ? `${draft.photos.length} photo${draft.photos.length === 1 ? '' : 's'} ready` : "Snap it while it's in front of you"}
                </span>
            </div>

            <div className="grid grid-cols-3 items-center px-7 pb-2 pt-6">
                <button type="button" className="flex flex-col items-center gap-1 text-xs font-semibold" onClick={() => libraryInput.current?.click()}>
                    <span className="flex h-12 w-12 items-center justify-center rounded-[12px] border-2 border-white bg-white/10">
                        <Icon name="journal" size={22} />
                    </span>
                    Library
                </button>
                <button type="button" aria-label="Take a photo" className="mx-auto flex h-20 w-20 items-center justify-center rounded-pill border-4 border-white" onClick={() => cameraInput.current?.click()}>
                    <span className="h-[62px] w-[62px] rounded-pill bg-white" />
                </button>
                {draft.photos.length > 0 ? (
                    <button type="button" className="flex flex-col items-center gap-1 text-xs font-semibold" onClick={() => navigate('/tell/new/kind')}>
                        <span className="flex h-12 w-12 items-center justify-center rounded-pill bg-[#7FCF76] text-[#0F2210]">
                            <Icon name="check" size={22} strokeWidth={2.2} />
                        </span>
                        Done
                    </button>
                ) : (
                    <span />
                )}
            </div>

            <button type="button" className="mx-auto mb-[max(24px,env(safe-area-inset-bottom))] mt-1 flex min-h-11 items-center gap-2 px-[18px] text-[15px] font-[650] text-[#CFE9C9]" onClick={() => navigate('/tell/new/kind')}>
                <Icon name="note" size={18} />
                Skip the photo, just words
            </button>

            <input ref={cameraInput} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFiles} />
            <input ref={libraryInput} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} />

            <Sheet open={closing} onClose={() => setClosing(false)} title="Keep this tell for later?">
                <div className="flex flex-col gap-2">
                    <p className="m-0 text-ink-2">It stays as a draft on the Journal until you finish or discard it.</p>
                    <button type="button" className="btn btn-primary" onClick={() => navigate('/journal')}>
                        Keep as a draft
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary btn-danger"
                        onClick={() => {
                            draftStore.reset();
                            navigate('/journal');
                        }}
                    >
                        Discard
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={() => setClosing(false)}>
                        Carry on
                    </button>
                </div>
            </Sheet>
        </div>
    );
}

// ------------------------------------------------------------------ step 2

export function NewTellKindStep() {
    const navigate = useNavigate();
    const {draft} = useDraft();
    const preview = draft.photos[0]?.previewUrl ?? null;

    const choose = (kind: Kind) => {
        draftStore.patch({kind, detail: kind === draft.kind ? draft.detail : null});
        navigate('/tell/new/words');
    };

    return (
        <div className="settle-in flex flex-col gap-[18px] pb-6">
            <header className="-mx-1 flex items-center justify-between">
                <button type="button" aria-label="Back" className="flex h-11 w-11 items-center justify-center rounded-pill" onClick={() => navigate('/tell/new')}>
                    <Icon name="back" size={22} />
                </button>
                <StepBars step={2} />
                <span className="w-11" />
            </header>

            <div className="flex items-center gap-[14px]">
                {preview && <img src={preview} alt="" className="h-[84px] w-[84px] shrink-0 rounded-[18px] object-cover shadow-1" />}
                <div>
                    <h1 className="m-0 font-display text-[30px] font-[750] leading-[1.05] tracking-[-0.02em]">What&rsquo;s this?</h1>
                    <p className="m-0 mt-1 text-sm text-ink-2">One tap. You can change it later.</p>
                </div>
            </div>

            <KindGrid value={draft.kind} onChoose={choose} />
        </div>
    );
}

// ------------------------------------------------------------------ step 3

export function NewTellWordsStep() {
    const navigate = useNavigate();
    const {draft, ready} = useDraft();
    const defaults = useLatestDefaults();
    const {create} = usePostMutations();
    const addFollowUp = useAddFollowUp();
    const [prefilled, setPrefilled] = useState(false);

    // "Place and plant remember the last value used": prefill once, only into an untouched draft.
    useEffect(() => {
        if (prefilled || !ready || !defaults.data) return;
        setPrefilled(true);
        const d = draftStore.get().draft;
        if (d.plants.length === 0 && d.placeId === null && d.text === '') {
            draftStore.patch({plants: defaults.data.plant ? [defaults.data.plant] : [], placeId: defaults.data.placeId});
        }
    }, [defaults.data, prefilled, ready]);

    if (!ready) return <SkeletonList rows={5} />;
    if (!draft.kind) return <Navigate to="/tell/new/kind" replace />;

    const values: TellFormValues = {kind: draft.kind, detail: draft.detail, text: draft.text, plants: draft.plants, placeId: draft.placeId, whenLocal: draft.whenLocal || nowLocal(), quantity: draft.quantity, happy: draft.happy ?? false, followUp: draft.followUp ?? null};

    const save = () => {
        const unreadable = draft.photos.filter(p => p.error !== null).length;
        create.mutate(
            {
                kind: values.kind,
                text: values.text,
                whenLocal: values.whenLocal,
                whenOffsetMinutes: offsetMinutes(),
                plants: values.plants,
                placeId: values.placeId,
                quantity: values.quantity.trim() || null,
                detail: values.detail,
                happy: values.happy
            },
            {
                onSuccess: post => {
                    if (values.followUp) addFollowUp(post, values.followUp);
                    const photos = draftStore.takePhotos();
                    draftStore.reset();
                    uploadQueue.enqueue(post.id, photos.map(p => ({id: p.id, previewUrl: p.previewUrl, prepared: p.prepared})));
                    if (unreadable > 0) toast.problem(`${unreadable === 1 ? 'One photo' : `${unreadable} photos`} could not be read and ${unreadable === 1 ? 'was' : 'were'} left out.`);
                    navigate('/journal', {replace: true});
                }
            }
        );
    };

    const discard = async () => {
        if (await confirm({title: 'Discard this tell?', body: 'The words and photos you have added will go.', confirmLabel: 'Discard', destructive: true})) {
            draftStore.reset();
            navigate('/journal', {replace: true});
        }
    };

    const samePlant = defaults.data?.plant && draft.plants.length === 1 && draft.plants[0] === defaults.data.plant ? 'Same as last time.' : null;

    return (
        <TellForm
            values={values}
            onChange={patch => draftStore.patch(patch)}
            onBack={() => navigate('/tell/new/kind')}
            onChangeKind={() => navigate('/tell/new/kind')}
            onSubmit={save}
            submitting={create.isPending}
            error={create.error}
            plantHint={samePlant}
            photosSlot={
                <div className="flex gap-[10px] overflow-x-auto [scrollbar-width:none]">
                    {draft.photos.map(p => (
                        <div key={p.id} className="relative shrink-0" title={p.error ?? undefined}>
                            <img src={p.previewUrl} alt="" className={`h-[76px] w-[76px] rounded-[16px] object-cover ${p.error ? 'opacity-40' : ''}`} />
                            {p.error && <span className="absolute inset-x-1 bottom-1 rounded-sm bg-rust px-1 text-center text-[10px] font-bold text-white">unreadable</span>}
                            <button type="button" aria-label="Remove photo" className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-pill bg-ink text-ground" onClick={() => draftStore.removePhoto(p.id)}>
                                <Icon name="close" size={12} strokeWidth={2.5} />
                            </button>
                        </div>
                    ))}
                    {draft.photos.length < MAX_PHOTOS && (
                        <button type="button" className="flex h-[76px] w-[76px] shrink-0 flex-col items-center justify-center gap-1 rounded-[16px] border-[1.5px] border-dashed border-line text-xs font-semibold text-ink-2" onClick={() => navigate('/tell/new')}>
                            <Icon name="plus" size={20} />
                            add
                        </button>
                    )}
                </div>
            }
            footer={
                <button type="button" className="meta mt-2 self-center underline-offset-2 hover:underline" onClick={() => void discard()}>
                    Discard this tell
                </button>
            }
        />
    );
}
